import { useEffect, useState } from 'react';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useSearchWorkflowsLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { buildInternalSourcingWorkflowFilter } from 'modules/candidate-profile/utils/buildInternalSourcingWorkflowFilter';

/**
 * The name of a workflow handed over from the builder, so the confirm can say where these
 * people are going rather than "your workflow".
 *
 * Goes through the same eligibility filter as the picker, so a URL naming a workflow that
 * cannot accept internally-sourced candidates resolves to nothing and the caller falls back.
 */
export const useInternalSourcingWorkflowName = (
  workflowId: string | null
): string | null => {
  const organization = useOrganization();
  const [name, setName] = useState<string | null>(null);
  const [searchWorkflows] = useSearchWorkflowsLazyQuery();

  useEffect(() => {
    if (!workflowId) {
      setName(null);
      return;
    }

    const run = { isCancelled: false };

    const resolveName = async () => {
      try {
        const { data } = await searchWorkflows({
          variables: {
            filter: buildInternalSourcingWorkflowFilter({
              organizationId: organization.id,
              workflowId
            }),
            limit: 1
          }
        });

        const [workflow] = extractQueryListItems(data);
        if (!run.isCancelled) {
          setName(workflow?.name ?? null);
        }
      } catch {
        // Falling back to a generic phrase is better than blocking the confirm.
      }
    };

    resolveName();

    return () => {
      run.isCancelled = true;
    };
  }, [workflowId, organization.id]);

  return name;
};
